import 'lib/mobile_detect_wrapper';
import { Formik, Field, Form } from 'formik';
import * as Yup from 'yup';
import { Global } from '@emotion/react';
import { cssReset } from 'ui_system/token_auth/styles_root';
import RadioPair from 'ui_system/forms/RadioPair';
import { BaseSelect } from 'ui_system/forms/BaseSelect';
import {
  AccessibleCheckbox,
  greyBeforeClick,
} from 'ui_system/inputs/checkboxes';
import { TextField, FieldSection, Error } from 'ui_system/forms/FormStyles';
import { registerRequest } from './api';
import {
  Container,
  InputContainer,
  BareHR,
  FormH1,
  BodyText,
  TopSpacer,
  SubmitButton,
  Label,
  IndentedSubLabel,
  SwornText,
  RedLink,
} from './PrivacyStyles';
import { Logo } from './logo';

const textFieldProps = {
  css: TextField,
  type: 'text',
};

const validationSchema = Yup.object().shape({
  email: Yup.string()
    .email('Please enter a valid email address.')
    .required('Please enter an email address.'),
  fullName: Yup.string().trim().required('Please enter your name.'),
  stateOfResidency: Yup.object().required('Please select a state.'),
  operation: Yup.mixed()
    .nullable()
    .required('Delete your info or retrieve your info?'),
  sworn: Yup.boolean().oneOf(
    [true],
    'Please double check your entries and click here to confirm.'
  ),
});

const stateOptions = [
  { value: 'intl', label: 'Not in the United States' },
  { value: 'al', label: 'Alabama' },
  { value: 'ak', label: 'Alaska' },
  { value: 'as', label: 'American Samoa' },
  { value: 'az', label: 'Arizona' },
  { value: 'ar', label: 'Arkansas' },
  { value: 'ca', label: 'California' },
  { value: 'co', label: 'Colorado' },
  { value: 'ct', label: 'Connecticut' },
  { value: 'de', label: 'Delaware' },
  { value: 'dc', label: 'District of Columbia' },
  { value: 'fl', label: 'Florida' },
  { value: 'ga', label: 'Georgia' },
  { value: 'gu', label: 'Guam' },
  { value: 'hi', label: 'Hawaii' },
  { value: 'id', label: 'Idaho' },
  { value: 'il', label: 'Illinois' },
  { value: 'in', label: 'Indiana' },
  { value: 'ia', label: 'Iowa' },
  { value: 'ks', label: 'Kansas' },
  { value: 'ky', label: 'Kentucky' },
  { value: 'la', label: 'Louisiana' },
  { value: 'me', label: 'Maine' },
  { value: 'md', label: 'Maryland' },
  { value: 'ma', label: 'Massachusetts' },
  { value: 'mi', label: 'Michigan' },
  { value: 'mn', label: 'Minnesota' },
  { value: 'ms', label: 'Mississippi' },
  { value: 'mo', label: 'Missouri' },
  { value: 'mt', label: 'Montana' },
  { value: 'ne', label: 'Nebraska' },
  { value: 'nv', label: 'Nevada' },
  { value: 'nh', label: 'New Hampshire' },
  { value: 'nj', label: 'New Jersey' },
  { value: 'nm', label: 'New Mexico' },
  { value: 'ny', label: 'New York' },
  { value: 'nc', label: 'North Carolina' },
  { value: 'nd', label: 'North Dakota' },
  { value: 'mp', label: 'Northern Mariana Island' },
  { value: 'oh', label: 'Ohio' },
  { value: 'ok', label: 'Oklahoma' },
  { value: 'or', label: 'Oregon' },
  { value: 'pa', label: 'Pennsylvania' },
  { value: 'pr', label: 'Puerto Rico' },
  { value: 'ri', label: 'Rhode Island' },
  { value: 'sc', label: 'South Carolina' },
  { value: 'sd', label: 'South Dakota' },
  { value: 'tn', label: 'Tennessee' },
  { value: 'tx', label: 'Texas' },
  { value: 'ut', label: 'Utah' },
  { value: 'vt', label: 'Vermont' },
  { value: 'va', label: 'Virginia' },
  { value: 'vi', label: 'Virgin Islands' },
  { value: 'wa', label: 'Washington' },
  { value: 'wv', label: 'West Virginia' },
  { value: 'wi', label: 'Wisconsin' },
  { value: 'wy', label: 'Wyoming' },
];

const PrivacyRequestForm = (props) => {
  const { history } = props;
  const initialValues = {
    email: '',
    fullName: '',
    stateOfResidency: null,
    operation: null,
    sworn: false,
  };

  const onSubmit = (values, actions) => {
    if (values.stateOfResidency.value !== 'ca') {
      history.push('/privacy-not-eligible');
      return;
    }
    values.operation
      ? (values.operation = 'delete')
      : (values.operation = 'retrieve');
    // If the backend bugs, pop the submit button back up for a second try
    // after 3 seconds.
    setTimeout(() => {
      actions.setSubmitting(false);
    }, 3000);
    registerRequest(values)
      .then((response) => {
        if (response && response.status === 200) {
          history.push('/privacy-form-submitted');
        }
      })
      .catch((err) => {
        if (err?.response?.data?.error === 'duplicate') {
          history.push('/privacy-duplicate');
        }
      });
  };

  return (
    <div>
      <Global styles={cssReset({})} />
      <Logo />
      <BareHR />
      <Container>
        <Formik
          initialValues={initialValues}
          onSubmit={onSubmit}
          render={PrivacyRequestPresenter}
          validationSchema={validationSchema}
          validateOnChange={false}
          validateOnBlur={false}
        />
      </Container>
    </div>
  );
};

const PrivacyRequestPresenter = ({
  errors,
  isSubmitting,
  values,
  setFieldValue,
}) => {
  return (
    <Form>
      <div>
        <FormH1>
          California Consumer Privacy Act (CCPA)
          <br />
          Personal Information Request
        </FormH1>
        <BodyText style={{ textAlign: 'left' }}>
          Under the California Consumer Privacy Act ("CCPA"), California
          residents, subject to certain limitations under applicable law, have
          the right to (1) request access to personal information we have
          collected about you and (2) request that we delete personal
          information we have collected from you. To exercise these rights,
          please complete this form. For more information, refer to our complete{' '}
          <RedLink href="https://www.anyroad.com/privacy">
            Privacy Policy
          </RedLink>
          .
        </BodyText>
      </div>
      <FieldSection>
        <TopSpacer>
          <RadioPair
            field={{ name: 'operation', value: values.operation }}
            isStacked
            labels={[
              'I would like access to my personal information on AnyRoad.',
              'I would like to delete information relating to me on AnyRoad.',
            ]}
            circleSize="24px"
            simpleCircle
            fontSize="14px"
            values={[false, true]}
            handleToggle={setFieldValue}
            format="minimal"
          />
          {errors.operation && <Error>{errors.operation}</Error>}
          <IndentedSubLabel>
            Please note that if you choose to have your information deleted, you
            will not be able to receive updates for any pending transactions or
            upcoming experiences already booked through AnyRoad.
          </IndentedSubLabel>
        </TopSpacer>
      </FieldSection>
      <InputContainer>
        <Label>Full Name</Label>
        <FieldSection marginBottom={20}>
          <Field {...textFieldProps} name="fullName" />
          {errors.fullName && <Error>{errors.fullName}</Error>}
        </FieldSection>
        <Label>Email Address</Label>
        <FieldSection marginBottom={20}>
          <Field {...textFieldProps} name="email" />
          {errors.email && <Error>{errors.email}</Error>}
        </FieldSection>
        <Label>State of Residency</Label>
        <FieldSection marginBottom={30}>
          <Field
            name="stateOfResidency"
            render={() => (
              <BaseSelect
                value={stateOptions && stateOptions[values.stateOfResidency]}
                placeholder="select a state"
                onChange={(val) => setFieldValue('stateOfResidency', val)}
                options={stateOptions}
                styles={{ container: () => ({ width: '100%' }) }}
              />
            )}
          />
          {errors.stateOfResidency && <Error>{errors.stateOfResidency}</Error>}
        </FieldSection>
      </InputContainer>
      <FieldSection>
        <Field
          name="sworn"
          render={() => (
            <AccessibleCheckbox
              id="sworn"
              value={values.sworn}
              onChange={() => setFieldValue('sworn', !values.sworn)}
              size="24px"
              modifiers={[greyBeforeClick]}
              checked={values.sworn}
            >
              <SwornText>
                I declare under penalty of perjury that the information in this
                form is true and correct and that I am either the consumer
                identified in this request or authorized to make this request on
                behalf of the consumer.
              </SwornText>
            </AccessibleCheckbox>
          )}
        />
        {errors.sworn && <Error>{errors.sworn}</Error>}
      </FieldSection>
      <SubmitButton
        type="submit"
        disabled={isSubmitting}
        faded={!values.sworn}
        text="Submit"
      />
    </Form>
  );
};

export default PrivacyRequestForm;
